import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useLanguage, useTranslation } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, X, Camera, AlertCircle, Loader2, Globe, ChevronDown, ChevronRight, User, Heart, Briefcase, Baby, Cigarette, Palette } from "lucide-react";
import { INTERESTS_FR, INTERESTS_EN } from "../data/interests";

import { API } from "../config";

// Advanced profile options
const PROFILE_OPTIONS = {
  height: {
    label: { fr: "Taille", en: "Height" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "<150", label: { fr: "< 150 cm", en: "< 150 cm" } },
      { value: "150-159", label: { fr: "150-159 cm", en: "150-159 cm" } },
      { value: "160-169", label: { fr: "160-169 cm", en: "160-169 cm" } },
      { value: "170-179", label: { fr: "170-179 cm", en: "170-179 cm" } },
      { value: "180-189", label: { fr: "180-189 cm", en: "180-189 cm" } },
      { value: "190+", label: { fr: "190+ cm", en: "190+ cm" } }
    ]
  },
  body_type: {
    label: { fr: "Silhouette", en: "Body type" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "slim", label: { fr: "Mince", en: "Slim" } },
      { value: "athletic", label: { fr: "Athlétique", en: "Athletic" } },
      { value: "average", label: { fr: "Normal(e)", en: "Average" } },
      { value: "curvy", label: { fr: "Ronde", en: "Curvy" } },
      { value: "full", label: { fr: "Corpulent(e)", en: "Full" } }
    ]
  },
  eyes: {
    label: { fr: "Couleur des yeux", en: "Eye color" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "brown", label: { fr: "Marron", en: "Brown" } },
      { value: "blue", label: { fr: "Bleu", en: "Blue" } },
      { value: "green", label: { fr: "Vert", en: "Green" } },
      { value: "hazel", label: { fr: "Noisette", en: "Hazel" } },
      { value: "other", label: { fr: "Autre", en: "Other" } }
    ]
  },
  hair: {
    label: { fr: "Couleur des cheveux", en: "Hair color" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "brown", label: { fr: "Bruns", en: "Brown" } },
      { value: "blonde", label: { fr: "Blonds", en: "Blonde" } },
      { value: "black", label: { fr: "Noirs", en: "Black" } },
      { value: "red", label: { fr: "Roux", en: "Red" } },
      { value: "gray", label: { fr: "Gris", en: "Gray" } },
      { value: "bald", label: { fr: "Chauve", en: "Bald" } }
    ]
  },
  smoking: {
    label: { fr: "Fumeur", en: "Smoker" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "no", label: { fr: "Non", en: "No" } },
      { value: "occasional", label: { fr: "Occasionnel", en: "Occasional" } },
      { value: "yes", label: { fr: "Régulier", en: "Regular" } }
    ]
  },
  drinking: {
    label: { fr: "Alcool", en: "Alcohol" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "never", label: { fr: "Jamais", en: "Never" } },
      { value: "occasional", label: { fr: "Occasionnel", en: "Occasional" } },
      { value: "regular", label: { fr: "Régulier", en: "Regular" } }
    ]
  },
  children: {
    label: { fr: "Enfants", en: "Children" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "none", label: { fr: "Aucun", en: "None" } },
      { value: "has", label: { fr: "Avec moi", en: "Living with me" } },
      { value: "shared", label: { fr: "Garde partagée", en: "Shared custody" } }
    ]
  },
  wants_children: {
    label: { fr: "Souhaite des enfants", en: "Wants children" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "yes", label: { fr: "Oui", en: "Yes" } },
      { value: "no", label: { fr: "Non", en: "No" } },
      { value: "maybe", label: { fr: "Peut-être", en: "Maybe" } }
    ]
  },
  education: {
    label: { fr: "Niveau d'études", en: "Education" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "high_school", label: { fr: "Bac", en: "High school" } },
      { value: "bac2", label: { fr: "Bac +2", en: "Associate" } },
      { value: "bachelor", label: { fr: "Bac +3", en: "Bachelor" } },
      { value: "master", label: { fr: "Bac +5", en: "Master" } },
      { value: "phd", label: { fr: "Doctorat", en: "PhD" } }
    ]
  },
  profession: {
    label: { fr: "Profession", en: "Profession" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "student", label: { fr: "Étudiant(e)", en: "Student" } },
      { value: "employee", label: { fr: "Employé(e)", en: "Employee" } },
      { value: "manager", label: { fr: "Cadre", en: "Manager" } },
      { value: "freelance", label: { fr: "Indépendant", en: "Freelance" } },
      { value: "entrepreneur", label: { fr: "Entrepreneur", en: "Entrepreneur" } },
      { value: "retired", label: { fr: "Retraité(e)", en: "Retired" } }
    ]
  },
  relationship_type: {
    label: { fr: "Type de relation recherchée", en: "Relationship type" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "serious", label: { fr: "Sérieuse", en: "Serious" } },
      { value: "casual", label: { fr: "Occasionnelle", en: "Casual" } },
      { value: "friendship", label: { fr: "Amicale", en: "Friendship" } },
      { value: "undecided", label: { fr: "Je ne sais pas", en: "Not sure" } }
    ]
  },
  pets: {
    label: { fr: "Animaux", en: "Pets" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "none", label: { fr: "Aucun", en: "None" } },
      { value: "dog", label: { fr: "Chien", en: "Dog" } },
      { value: "cat", label: { fr: "Chat", en: "Cat" } },
      { value: "other", label: { fr: "Autre", en: "Other" } },
      { value: "loves", label: { fr: "Aime les animaux", en: "Loves animals" } }
    ]
  },
  personality: {
    label: { fr: "Personnalité", en: "Personality" },
    options: [
      { value: "", label: { fr: "Non spécifié", en: "Not specified" } },
      { value: "calm", label: { fr: "Calme", en: "Calm" } },
      { value: "dynamic", label: { fr: "Dynamique", en: "Dynamic" } },
      { value: "funny", label: { fr: "Drôle", en: "Funny" } },
      { value: "introvert", label: { fr: "Introverti(e)", en: "Introvert" } },
      { value: "extrovert", label: { fr: "Extraverti(e)", en: "Extrovert" } }
    ]
  }
};

// Composant Label avec astérisque rouge pour les champs obligatoires
function RequiredLabel({ children, htmlFor, className = "" }) {
  return (
    <Label htmlFor={htmlFor} className={`text-white flex items-center gap-1 ${className}`}>
      {children}
      <span className="text-red-500 text-lg">*</span>
    </Label>
  );
}

// Composant pour afficher les photos (gère les photos uploadées en base64)
function PhotoDisplay({ src, alt }) {
  const [imageSrc, setImageSrc] = useState(src);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (src && src.includes("/api/photos/")) {
      setLoading(true);
      fetch(src, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("session_token")}`
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data.data) {
            setImageSrc(data.data);
          }
        })
        .catch(err => console.error("Error loading photo:", err))
        .finally(() => setLoading(false));
    } else if (src) {
      setImageSrc(src);
    }
  }, [src]);
  
  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return <img src={imageSrc} alt={alt} className="w-full h-full object-cover" />;
}

// Section pliable pour les options avancées
function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-primary" />
          <span className="font-medium text-white">{title}</span>
        </div>
        <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="p-4 space-y-4 bg-muted/10">
          {children}
        </div>
      )}
    </div>
  );
}

export default function EditProfile() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  
  // Location state
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [customCity, setCustomCity] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    gender: "",
    bio: "",
    country: "FR",
    region: "",
    city: "",
    location: "",
    looking_for: "",
    interested_in: "",
    interests: [],
    photos: [],
    main_photo: "",
    // Advanced profile fields
    height: "",
    body_type: "",
    eyes: "",
    hair: "",
    smoking: "",
    drinking: "",
    children: "",
    wants_children: "",
    education: "",
    profession: "",
    relationship_type: "",
    pets: "",
    personality: ""
  });

  const INTERESTS = language === 'fr' ? INTERESTS_FR : INTERESTS_EN;

  // Load countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await fetch(`${API}/locations/countries?lang=${language}`);
        const data = await response.json();
        setCountries(data.countries || []);
      } catch (error) {
        console.error("Error loading countries:", error);
      }
    };
    loadCountries();
  }, [language]);

  // Load regions when country changes
  useEffect(() => {
    if (formData.country) {
      const loadRegions = async () => {
        setLoadingLocations(true);
        try {
          const response = await fetch(`${API}/locations/regions/${formData.country}?lang=${language}`);
          const data = await response.json();
          setRegions(data.regions || []);
          if (!formData.region) {
            setCities([]);
          }
        } catch (error) {
          console.error("Error loading regions:", error);
        } finally {
          setLoadingLocations(false);
        }
      };
      loadRegions();
    }
  }, [formData.country, language]);

  // Load cities when region changes
  useEffect(() => {
    if (formData.country && formData.region) {
      const loadCities = async () => {
        setLoadingLocations(true);
        try {
          const response = await fetch(`${API}/locations/cities/${formData.country}/${formData.region}`);
          const data = await response.json();
          setCities(data.cities || []);
        } catch (error) {
          console.error("Error loading cities:", error);
        } finally {
          setLoadingLocations(false);
        }
      };
      loadCities();
    }
  }, [formData.country, formData.region]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        age: user.age || "",
        gender: user.gender || "",
        bio: user.bio || "",
        country: user.country || "FR",
        region: user.region || "",
        city: user.city || "",
        location: user.location || "",
        looking_for: user.looking_for || "",
        interested_in: user.interested_in || "",
        interests: Array.isArray(user.interests) ? user.interests : [],
        photos: Array.isArray(user.photos) ? user.photos : [],
        main_photo: user.main_photo || (user.photos && user.photos[0]) || "",
        // Advanced fields
        height: user.height || "",
        body_type: user.body_type || "",
        eyes: user.eyes || "",
        hair: user.hair || "",
        smoking: user.smoking || "",
        drinking: user.drinking || "",
        children: user.children || "",
        wants_children: user.wants_children || "",
        education: user.education || "",
        profession: user.profession || "",
        relationship_type: user.relationship_type || "",
        pets: user.pets || "",
        personality: user.personality || ""
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = language === 'fr' ? "Le prénom est obligatoire" : "First name is required";
    }
    
    if (!formData.age || formData.age < 18 || formData.age > 99) {
      newErrors.age = language === 'fr' ? "L'âge doit être entre 18 et 99 ans" : "Age must be between 18 and 99";
    }
    
    if (!formData.gender) {
      newErrors.gender = language === 'fr' ? "Le genre est obligatoire" : "Gender is required";
    }
    
    if (!formData.bio.trim()) {
      newErrors.bio = language === 'fr' ? "La description est obligatoire" : "Bio is required";
    } else if (formData.bio.length < 10) {
      newErrors.bio = language === 'fr' ? "La description doit faire au moins 10 caractères" : "Bio must be at least 10 characters";
    }
    
    if (!formData.region) {
      newErrors.region = language === 'fr' ? "Le département est obligatoire" : "Region is required";
    }
    
    if (!formData.city) {
      newErrors.city = language === 'fr' ? "La ville est obligatoire" : "City is required";
    }
    
    if (formData.photos.length === 0) {
      newErrors.photos = language === 'fr' ? "Au moins une photo est obligatoire" : "At least one photo is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert(language === 'fr' ? "Veuillez remplir tous les champs obligatoires (marqués d'un *)" : "Please fill in all required fields (marked with *)");
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem("session_token");
      
      const dataToSend = {
        name: formData.name.trim(),
        age: parseInt(formData.age),
        gender: formData.gender,
        bio: formData.bio.trim(),
        country: formData.country,
        region: formData.region,
        city: formData.city,
        looking_for: formData.looking_for || null,
        interested_in: formData.interested_in || null,
        interests: formData.interests,
        photos: formData.photos,
        main_photo: formData.main_photo || formData.photos[0] || null,
        // Advanced fields
        height: formData.height || null,
        body_type: formData.body_type || null,
        eyes: formData.eyes || null,
        hair: formData.hair || null,
        smoking: formData.smoking || null,
        drinking: formData.drinking || null,
        children: formData.children || null,
        wants_children: formData.wants_children || null,
        education: formData.education || null,
        profession: formData.profession || null,
        relationship_type: formData.relationship_type || null,
        pets: formData.pets || null,
        personality: formData.personality || null
      };
      
      const response = await fetch(`${API}/users/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setUser(data);
        localStorage.setItem("user", JSON.stringify(data));
        alert(language === 'fr' ? "Profil mis à jour avec succès !" : "Profile updated successfully!");
        navigate("/profile");
      } else {
        alert(data.detail || (language === 'fr' ? "Erreur lors de la mise à jour" : "Error updating profile"));
      }
    } catch (error) {
      console.error("Update error:", error);
      alert(language === 'fr' ? "Erreur de connexion" : "Connection error");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert(language === 'fr' ? "Veuillez sélectionner une image" : "Please select an image");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert(language === 'fr' ? "L'image ne doit pas dépasser 5Mo" : "Image must not exceed 5MB");
      return;
    }
    
    if (formData.photos.length >= 6) {
      alert(language === 'fr' ? "Maximum 6 photos autorisées" : "Maximum 6 photos allowed");
      return;
    }
    
    setUploadingPhoto(true);
    
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/users/photos/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ photo: base64 })
      });
      
      if (response.ok) {
        const data = await response.json();
        const newPhotoUrl = `/api/photos/${data.photo_id}`;
        const newPhotos = [...formData.photos, newPhotoUrl];
        
        setFormData(prev => ({
          ...prev,
          photos: newPhotos,
          main_photo: prev.main_photo || newPhotoUrl
        }));
        
        if (errors.photos) {
          setErrors(prev => ({ ...prev, photos: null }));
        }
      } else {
        alert(language === 'fr' ? "Erreur lors de l'upload" : "Upload error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(language === 'fr' ? "Erreur lors de l'upload" : "Upload error");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removePhoto = async (photoUrl) => {
    const newPhotos = formData.photos.filter(p => p !== photoUrl);
    let newMainPhoto = formData.main_photo;
    
    if (formData.main_photo === photoUrl) {
      newMainPhoto = newPhotos[0] || "";
    }
    
    setFormData(prev => ({
      ...prev,
      photos: newPhotos,
      main_photo: newMainPhoto
    }));
    
    if (photoUrl.includes("/api/photos/")) {
      try {
        const photoId = photoUrl.replace("/api/photos/", "");
        const token = localStorage.getItem("session_token");
        await fetch(`${API}/users/photos/${photoId}`, {
          method: "DELETE",
          headers: { "Authorization": `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Error deleting photo:", error);
      }
    }
  };

  const setMainPhoto = (photoUrl) => {
    setFormData(prev => ({ ...prev, main_photo: photoUrl }));
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const renderSelect = (field) => {
    const config = PROFILE_OPTIONS[field];
    return (
      <div className="space-y-2">
        <Label className="text-white text-sm">{config.label[language]}</Label>
        <select
          value={formData[field]}
          onChange={(e) => setFormData(prev => ({ ...prev, [field]: e.target.value }))}
          className="w-full h-10 px-3 rounded-lg bg-muted/50 border border-white/10 focus:border-primary text-white text-sm"
        >
          {config.options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label[language]}</option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/profile")}
            className="text-white"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h1 className="font-heading text-xl font-bold text-white">
            {t('editProfile.title')}
          </h1>
        </div>
      </header>

      {/* Required fields notice */}
      <div className="max-w-lg mx-auto px-4 pt-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-400 text-sm">
            {language === 'fr' 
              ? "Les champs marqués d'un * sont obligatoires pour apparaître dans les recherches"
              : "Fields marked with * are required to appear in searches"
            }
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto p-4 space-y-6">
        {/* Photos Section */}
        <div className="space-y-4">
          <RequiredLabel className="text-lg font-semibold flex items-center gap-2">
            <Camera className="w-5 h-5" />
            {t('editProfile.photosSection')}
          </RequiredLabel>
          <p className="text-sm text-muted-foreground">{t('editProfile.maxPhotos')}</p>
          
          {errors.photos && (
            <p className="text-red-500 text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.photos}
            </p>
          )}
          
          <div className="grid grid-cols-3 gap-3">
            {formData.photos.map((photo, index) => (
              <div 
                key={index} 
                className={`relative aspect-square rounded-xl overflow-hidden border-2 ${
                  formData.main_photo === photo ? 'border-primary' : 'border-transparent'
                }`}
              >
                <PhotoDisplay src={photo.startsWith('/api') ? `${API}${photo.replace('/api', '')}` : photo} alt={`Photo ${index + 1}`} />
                <button
                  type="button"
                  onClick={() => removePhoto(photo)}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                {formData.main_photo !== photo && (
                  <button
                    type="button"
                    onClick={() => setMainPhoto(photo)}
                    className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs py-1 rounded"
                  >
                    {t('profile.setAsMain')}
                  </button>
                )}
                {formData.main_photo === photo && (
                  <div className="absolute bottom-2 left-2 right-2 bg-primary text-white text-xs py-1 rounded text-center">
                    {t('profile.mainPhoto')}
                  </div>
                )}
              </div>
            ))}
            
            {formData.photos.length < 6 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-all ${
                  errors.photos ? 'border-red-500' : 'border-white/20 hover:border-primary/50'
                }`}
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                ) : (
                  <>
                    <Camera className={`w-8 h-8 ${errors.photos ? 'text-red-500' : 'text-muted-foreground'}`} />
                    <span className={`text-xs ${errors.photos ? 'text-red-500' : 'text-muted-foreground'}`}>{t('profile.addPhoto')}</span>
                  </>
                )}
              </button>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            className="hidden"
          />
        </div>

        {/* Basic Info */}
        <div className="space-y-4">
          <Label className="text-white text-lg font-semibold">{t('editProfile.basicInfo')}</Label>
          
          <div className="space-y-2">
            <RequiredLabel htmlFor="name">{t('editProfile.name')}</RequiredLabel>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className={`bg-muted/50 border-transparent focus:border-primary ${errors.name ? 'border-red-500 border' : ''}`}
              placeholder={language === 'fr' ? "Votre prénom" : "Your first name"}
            />
            {errors.name && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <RequiredLabel htmlFor="age">{t('profile.age')}</RequiredLabel>
              <Input
                id="age"
                type="number"
                min="18"
                max="99"
                value={formData.age}
                onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                className={`bg-muted/50 border-transparent focus:border-primary ${errors.age ? 'border-red-500 border' : ''}`}
              />
              {errors.age && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.age}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <RequiredLabel htmlFor="gender">{t('editProfile.gender')}</RequiredLabel>
              <select
                id="gender"
                value={formData.gender}
                onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                className={`w-full h-10 px-3 rounded-md bg-muted/50 focus:border-primary text-white ${errors.gender ? 'border-red-500 border' : 'border-transparent'}`}
              >
                <option value="">{language === 'fr' ? "Sélectionner" : "Select"}</option>
                <option value="male">{t('editProfile.male')}</option>
                <option value="female">{t('editProfile.female')}</option>
                <option value="other">{t('editProfile.other')}</option>
              </select>
              {errors.gender && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.gender}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="space-y-2">
          <RequiredLabel htmlFor="bio" className="text-lg font-semibold">{t('editProfile.aboutMe')}</RequiredLabel>
          <Textarea
            id="bio"
            value={formData.bio}
            onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
            className={`bg-muted/50 border-transparent focus:border-primary min-h-[100px] ${errors.bio ? 'border-red-500 border' : ''}`}
            placeholder={t('editProfile.bioPlaceholder')}
          />
          {errors.bio && (
            <p className="text-red-500 text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.bio}
            </p>
          )}
        </div>

        {/* Location Section */}
        <div className="space-y-4">
          <Label className="text-white text-lg font-semibold flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t('editProfile.locationSection')}
            <span className="text-red-500 text-lg">*</span>
          </Label>
          
          {/* Country */}
          <div className="space-y-2">
            <RequiredLabel>{language === 'fr' ? "Pays" : "Country"}</RequiredLabel>
            <div className="relative">
              <select
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  country: e.target.value,
                  region: "",
                  city: ""
                }))}
                className="w-full h-12 px-4 pr-10 rounded-xl bg-muted/50 border border-white/10 focus:border-primary text-white appearance-none"
              >
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          {/* Region */}
          <div className="space-y-2">
            <RequiredLabel>{t('editProfile.department')}</RequiredLabel>
            <div className="relative">
              <select
                value={formData.region}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  region: e.target.value,
                  city: ""
                }))}
                disabled={!formData.country || loadingLocations}
                className={`w-full h-12 px-4 pr-10 rounded-xl bg-muted/50 focus:border-primary text-white appearance-none disabled:opacity-50 ${errors.region ? 'border-red-500 border' : 'border border-white/10'}`}
              >
                <option value="">{t('editProfile.selectDepartment')}</option>
                {regions.map(region => (
                  <option key={region.code} value={region.code}>
                    {region.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
            </div>
            {errors.region && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.region}
              </p>
            )}
          </div>

          {/* City */}
          <div className="space-y-2">
            <RequiredLabel>{t('editProfile.city')}</RequiredLabel>
            <div className="relative">
              <select
                value={formData.city === customCity && customCity ? "__other__" : formData.city}
                onChange={(e) => {
                  if (e.target.value === "__other__") {
                    setFormData(prev => ({ ...prev, city: "" }));
                  } else {
                    setFormData(prev => ({ ...prev, city: e.target.value }));
                    setCustomCity("");
                  }
                }}
                disabled={!formData.region || loadingLocations}
                className={`w-full h-12 px-4 pr-10 rounded-xl bg-muted/50 focus:border-primary text-white appearance-none disabled:opacity-50 ${errors.city ? 'border-red-500 border' : 'border border-white/10'}`}
              >
                <option value="">{t('editProfile.selectCity')}</option>
                {cities.map(city => (
                  <option key={city.name} value={city.name}>
                    {city.name}
                  </option>
                ))}
                <option value="__other__">{language === 'fr' ? "Autre ville..." : "Other city..."}</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
            </div>
            
            {((formData.city === "" && formData.region) || customCity !== "") && (
              <div className="space-y-2">
                <Input
                  value={customCity}
                  onChange={(e) => {
                    setCustomCity(e.target.value);
                    setFormData(prev => ({ ...prev, city: e.target.value }));
                  }}
                  placeholder={language === 'fr' ? "Saisissez le nom de votre ville" : "Enter your city name"}
                  className="bg-muted/50 border-white/10 focus:border-primary"
                />
              </div>
            )}
            
            {errors.city && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.city}
              </p>
            )}
          </div>
          
          {loadingLocations && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              {language === 'fr' ? "Chargement..." : "Loading..."}
            </div>
          )}
        </div>

        {/* Looking For & Interested In */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-white">
              {language === 'fr' ? "Je recherche" : "Looking for"}
            </Label>
            <select
              value={formData.looking_for}
              onChange={(e) => setFormData(prev => ({ ...prev, looking_for: e.target.value }))}
              className="w-full h-10 px-3 rounded-lg bg-muted/50 border border-white/10 focus:border-primary text-white text-sm"
            >
              <option value="">{language === 'fr' ? "Sélectionner" : "Select"}</option>
              <option value="relationship">{language === 'fr' ? "Relation sérieuse" : "Serious"}</option>
              <option value="casual">{language === 'fr' ? "Sans prise de tête" : "Casual"}</option>
              <option value="friendship">{language === 'fr' ? "Amitié" : "Friendship"}</option>
              <option value="undecided">{language === 'fr' ? "Je ne sais pas" : "Not sure"}</option>
            </select>
          </div>

          <div className="space-y-2">
            <RequiredLabel>
              {language === 'fr' ? "Intéressé(e) par" : "Interested in"}
            </RequiredLabel>
            <select
              value={formData.interested_in}
              onChange={(e) => setFormData(prev => ({ ...prev, interested_in: e.target.value }))}
              className="w-full h-10 px-3 rounded-lg bg-muted/50 border border-white/10 focus:border-primary text-white text-sm"
            >
              <option value="">{language === 'fr' ? "Sélectionner" : "Select"}</option>
              <option value="women">{language === 'fr' ? "Femmes" : "Women"}</option>
              <option value="men">{language === 'fr' ? "Hommes" : "Men"}</option>
              <option value="both">{language === 'fr' ? "Les deux" : "Both"}</option>
            </select>
          </div>
        </div>

        {/* Advanced Profile Sections */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            {language === 'fr' ? "Informations complémentaires" : "Additional Information"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {language === 'fr' ? "Ces informations sont facultatives mais aident à trouver des personnes compatibles" : "These are optional but help find compatible people"}
          </p>

          {/* Physical */}
          <CollapsibleSection 
            title={language === 'fr' ? "Physique" : "Physical"} 
            icon={User}
          >
            <div className="grid grid-cols-2 gap-3">
              {renderSelect('height')}
              {renderSelect('body_type')}
              {renderSelect('eyes')}
              {renderSelect('hair')}
            </div>
          </CollapsibleSection>

          {/* Lifestyle */}
          <CollapsibleSection 
            title={language === 'fr' ? "Habitudes" : "Lifestyle"} 
            icon={Cigarette}
          >
            <div className="grid grid-cols-2 gap-3">
              {renderSelect('smoking')}
              {renderSelect('drinking')}
              {renderSelect('pets')}
              {renderSelect('personality')}
            </div>
          </CollapsibleSection>

          {/* Family */}
          <CollapsibleSection 
            title={language === 'fr' ? "Famille" : "Family"} 
            icon={Baby}
          >
            <div className="grid grid-cols-2 gap-3">
              {renderSelect('children')}
              {renderSelect('wants_children')}
            </div>
          </CollapsibleSection>

          {/* Work & Education */}
          <CollapsibleSection 
            title={language === 'fr' ? "Études & Travail" : "Education & Work"} 
            icon={Briefcase}
          >
            <div className="grid grid-cols-2 gap-3">
              {renderSelect('education')}
              {renderSelect('profession')}
            </div>
          </CollapsibleSection>

          {/* Relationship */}
          <CollapsibleSection 
            title={language === 'fr' ? "Relation" : "Relationship"} 
            icon={Heart}
          >
            {renderSelect('relationship_type')}
          </CollapsibleSection>
        </div>

        {/* Interests */}
        <div className="space-y-4">
          <Label className="text-white text-lg font-semibold">{t('editProfile.interestsSection')}</Label>
          <p className="text-sm text-muted-foreground">
            {language === 'fr' 
              ? `${formData.interests.length} sélectionné(s)`
              : `${formData.interests.length} selected`
            }
          </p>
          <div className="flex flex-wrap gap-2">
            {INTERESTS.map(interest => (
              <button
                key={interest}
                type="button"
                onClick={() => toggleInterest(interest)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  formData.interests.includes(interest)
                    ? "bg-primary text-white"
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
              >
                {interest}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-14 rounded-2xl bg-primary text-white font-bold text-lg"
        >
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            t('editProfile.saveChanges')
          )}
        </Button>
      </form>
    </div>
  );
}
